import React, { memo, useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Form, Formik } from 'formik'
import type { CustomerProductName } from 'src/constants/customer/enums'
import type { CustomerProduct } from 'src/interfaces/customerProduct'
import { CustomerProductFormContent } from './CustomerProductFormContent'
import { buildCustomerProductFormValidator } from './CustomerProductForm.validator'
import type {
  CustomerProductFormMode,
  CustomerProductFormValues,
} from './CustomerProductForm.interface'

// Registered here as well as in utils/customerProductList, so seeding the pickers as UTC does
// not depend on this module's import order. `extend` is idempotent.
dayjs.extend(utc)

interface Props {
  formId: string
  mode: CustomerProductFormMode
  product: CustomerProduct | null
  availableProducts: CustomerProductName[]
  onSubmit(values: CustomerProductFormValues): void
}

const CustomerProductFormComponent = ({
  formId,
  mode,
  product,
  availableProducts,
  onSubmit,
}: Props) => {
  const initialValues = useMemo<CustomerProductFormValues>(() => {
    if (mode === 'edit' && product) {
      return {
        product: product.product,
        // Parsed as UTC to match how the grid renders it — a local parse would seed the
        // pickers with a time shifted by the admin's offset.
        startTime: product.startTime ? dayjs.utc(product.startTime) : null,
        endTime: product.endTime ? dayjs.utc(product.endTime) : null,
      }
    }

    return {
      product: '',
      startTime: null,
      endTime: null,
    }
  }, [mode, product])

  const validationSchema = useMemo(() => {
    return buildCustomerProductFormValidator(mode)
  }, [mode])

  const handleSubmit = useCallback(
    (values: CustomerProductFormValues) => {
      onSubmit(values)
    },
    [onSubmit],
  )

  return (
    <Formik<CustomerProductFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      // Reopening the modal on a different row must reseed the fields.
      enableReinitialize
      onSubmit={handleSubmit}
    >
      <Form id={formId}>
        <CustomerProductFormContent
          mode={mode}
          product={product}
          availableProducts={availableProducts}
        />
      </Form>
    </Formik>
  )
}

export const CustomerProductForm = memo(CustomerProductFormComponent)
